import React, { useCallback, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Button,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Comment as CommentIcon,
  DeleteSweep as ClearIcon,
} from '@mui/icons-material';
import { Visibility, CommentMode, RecipeGroupState, RowType, RecipeColumnState, RecipeState } from './types';
import { GroupStore } from './stores';
import RecipeCell from './RecipeCell';
import RecipeRowActions from './RecipeRowActions';
import { resolveRecipeColumnBackground } from './columnStyle';
import { FormulaScope as PlainFormulaScope } from './FormulaScope';
import { isIfConditionColumnVisibleOnGroup } from './visibilityUtils';
import { log } from '@/lib/utils/logger';

export interface RecipeGroupProps {
  group: GroupStore | RecipeGroupState; // MobX store in edit mode, plain data in readonly
  groupIndex: number;
  readonly: boolean;
  /** Full recipe in readonly mode — needed for cross-group IfCondition rules (e.g. MOCVD precursors). */
  recipeContext?: RecipeState;
  onAddGroup?: () => void;
  onRemoveGroup?: () => void;
}

const isGroupStore = (group: GroupStore | RecipeGroupState): group is GroupStore => {
  return 'addRow' in group && 'removeRow' in group;
};

const RecipeGroup = observer(
  ({ group, groupIndex: _groupIndex, readonly, recipeContext, onAddGroup, onRemoveGroup }: RecipeGroupProps) => {
    const [rowPendingDelete, setRowPendingDelete] = useState<number | null>(null);
    const [clearGroupRequested, setClearGroupRequested] = useState(false);
    const addRowButtonRef = useRef<HTMLButtonElement | null>(null);
    const tableRef = useRef<HTMLTableElement | null>(null);
    const pendingFocusColumnIndexRef = useRef<number | null>(null);

    // One snapshot for all IfCondition columns: avoids N× toState() and keeps MobX tracking
    // (do not wrap in useMemo with narrow deps — row/cell changes must re-run this filter).
    const mobxRecipeSnapshot = isGroupStore(group) ? group.getRecipeStateSnapshot() : null;
    const readonlyFormulaScope =
      !isGroupStore(group) && recipeContext ? new PlainFormulaScope(() => recipeContext) : undefined;
    const readonlyRecipeState: RecipeState =
      recipeContext ??
      ({
        templateName: '',
        groups: [group as RecipeGroupState],
        runtimeChangedManually: false,
      } as RecipeState);

    const visibleColumns = group.columns.filter((col) => {
      if (col.visibility === Visibility.IfReadonly) return readonly;
      if (col.visibility === Visibility.IfModifiable) return !readonly;
      if (col.visibility === Visibility.IfCondition) {
        if (isGroupStore(group) && mobxRecipeSnapshot) {
          return group.isIfConditionColumnVisible(col, readonly, mobxRecipeSnapshot);
        }
        // Optional fast path: precomputed column symbols (see RecipeGroupState.calculatedVisibility).
        if (!isGroupStore(group) && group.calculatedVisibility) {
          return group.calculatedVisibility.has(col.symbol);
        }
        if (!isGroupStore(group)) {
          return isIfConditionColumnVisibleOnGroup(col, group, readonly, readonlyRecipeState, readonlyFormulaScope);
        }
        return col.visible || false;
      }
      if (col.visibility === Visibility.Always || col.visible) return true;
      if (col.visibility === Visibility.Never || col.visibility === Visibility.OnlyForUpload) return false;
      return col.visible || false;
    });

    const handleCellChange = useCallback(
      (rowIndex: number, columnSymbol: string, value: any) => {
        if (!isGroupStore(group) || readonly) return;
        const row = group.rows[rowIndex];
        if (!row) {
          log.warn(`[RecipeGroup] Row ${rowIndex} not found`);
          return;
        }
        log.debug(`[RecipeGroup] Cell changed: ${group.symbol}.${row.symbol}.${columnSymbol} = ${value}`);
        row.setValue(columnSymbol, value);
      },
      [group, readonly]
    );

    const handleAddRow = () => {
      if (!isGroupStore(group) || readonly) return;
      group.addRow();
      // If Add Row came from keyboard navigation at table bottom, focus same column in the new row.
      const targetColumnIndex = pendingFocusColumnIndexRef.current;
      pendingFocusColumnIndexRef.current = null;
      if (targetColumnIndex != null) {
        window.requestAnimationFrame(() => {
          const table = tableRef.current;
          if (!table) return;
          const dataRows = table.querySelectorAll('tbody > tr:not(.comment-row)');
          if (dataRows.length === 0) return;
          const newDataRow = dataRows[dataRows.length - 1] as HTMLTableRowElement;
          const targetCell = newDataRow.cells[targetColumnIndex];
          if (!targetCell) return;
          const focusTarget = targetCell.querySelector(
            '[role="button"], [tabindex]:not([tabindex="-1"]), input, select, textarea'
          ) as HTMLElement | null;
          focusTarget?.focus();
        });
      }
      log.debug(`[RecipeGroup] Row added to group ${group.symbol}`);
    };

    const handleArrowDownAtLastRow = useCallback((columnIndex: number) => {
      pendingFocusColumnIndexRef.current = columnIndex;
      addRowButtonRef.current?.focus();
    }, []);

    const handleRemoveRow = async (rowIndex: number) => {
      if (!isGroupStore(group) || readonly) return;
      setRowPendingDelete(rowIndex);
    };

    const handleMoveRow = (rowIndex: number, direction: 'up' | 'down') => {
      if (!isGroupStore(group) || readonly) return;
      const targetIndex = direction === 'up' ? rowIndex - 1 : rowIndex + 1;
      if (targetIndex < 0 || targetIndex >= group.rows.length) return;
      const temp = group.rows[rowIndex];
      group.rows[rowIndex] = group.rows[targetIndex];
      group.rows[targetIndex] = temp;
      log.debug(`[RecipeGroup] Row ${rowIndex} moved ${direction} in group ${group.symbol}`);
    };

    const handleToggleAllComments = () => {
      if (readonly) return;
      const newValue = !group.rows[0]?.showComment;
      group.rows.forEach((row) => {
        row.showComment = newValue;
      });
    };

    const handleClearGroup = () => {
      if (!isGroupStore(group) || readonly) return;
      setClearGroupRequested(true);
    };

    const handleConfirmRemoveRow = () => {
      if (!isGroupStore(group) || readonly || rowPendingDelete == null) return;
      const rowIndex = rowPendingDelete;
      setRowPendingDelete(null);
      group.removeRow(rowIndex);
      log.debug(`[RecipeGroup] Row ${rowIndex} removed from group ${group.symbol}`);
    };

    const handleConfirmClearGroup = () => {
      if (!isGroupStore(group) || readonly) return;
      setClearGroupRequested(false);
      group.clearGroup();
      log.debug(`[RecipeGroup] Cleared group ${group.symbol}`);
    };

    const columnBackground = (column: RecipeColumnState) => resolveRecipeColumnBackground(column, group.color);

    return (
      <Box
        className={`recipe-group-container ${!group.scrolling ? 'no-scrolling' : ''}`}
        style={{ '--group-color': group.color } as React.CSSProperties}
      >
        <Box className="recipe-group-header">
          <h4 className="recipe-group-title">{group.name}</h4>
          {!readonly && group.comments && group.comments !== CommentMode.OFF ? (
            <Tooltip title="Toggle all comments">
              <IconButton size="small" onClick={handleToggleAllComments}>
                <CommentIcon />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>

        <Box className={group.scrolling ? 'recipe-table-scrolling' : ''}>
          <table ref={tableRef} className="table table-condensed table-bordered recipe-table">
            <thead>
              <tr>
                {!readonly ? <th className="recipe-actions-column"></th> : null}
                {visibleColumns.map((column) => (
                  <th
                    key={column.symbol}
                    className="recipe-column-header"
                    title={column.symbol}
                    style={{
                      minWidth: column.width || 'auto',
                      backgroundColor: columnBackground(column),
                    }}
                  >
                    {column.name}
                  </th>
                ))}
              </tr>
              <tr>
                {!readonly ? <th className="recipe-actions-column"></th> : null}
                {visibleColumns.map((column) => (
                  <th
                    key={`${column.symbol}-unit`}
                    className="recipe-column-header recipe-column-unit"
                    style={{ backgroundColor: columnBackground(column) }}
                  >
                    {column.unit ? ` [${column.unit}]` : null}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {group.rows.map((row, rowIndex) => (
                <React.Fragment key={row.id || rowIndex}>
                  <tr>
                    {!readonly ? (
                      <td className="recipe-actions-column" rowSpan={row.showComment ? 2 : 1}>
                        <RecipeRowActions
                          rowIndex={rowIndex}
                          totalRows={group.rows.length}
                          readonly={readonly}
                          canAddRow={group.canAddGroup}
                          groupComments={group.comments}
                          groupRowType={group.rowType}
                          onRemoveRow={handleRemoveRow}
                          onMoveRow={handleMoveRow}
                          onToggleComment={(idx) => {
                            if (readonly) return;
                            const targetRow = group.rows[idx];
                            if (targetRow) targetRow.showComment = !targetRow.showComment;
                          }}
                        />
                      </td>
                    ) : null}

                    {visibleColumns.map((column) => {
                      // Port from Knockout: cells only span 2 rows when showing comment AND column is "symbol" or "ts"
                      const hasCommentRow = row.showComment && group.comments !== CommentMode.OFF;
                      const isTwoLineCell = hasCommentRow && (column.symbol === 'symbol' || column.symbol === 'ts');
                      const rowSpan = isTwoLineCell ? 2 : 1;

                      return (
                        <td
                          key={`${row.id || rowIndex}-${column.symbol}`}
                          rowSpan={rowSpan}
                          className={column.isNumber || column.isScientific ? 'recipe-numeric-cell' : undefined}
                          style={{ backgroundColor: columnBackground(column) }}
                        >
                          <RecipeCell
                            row={row}
                            column={column}
                            groupColor={group.color}
                            groupSymbol={group.symbol}
                            readonly={readonly}
                            onValueChange={(value) => handleCellChange(rowIndex, column.symbol, value)}
                            onArrowDownAtLastRow={
                              !readonly &&
                              isGroupStore(group) &&
                              group.canAddGroup &&
                              group.rowType !== RowType.One &&
                              rowIndex === group.rows.length - 1
                                ? handleArrowDownAtLastRow
                                : undefined
                            }
                            isTwoLine={isTwoLineCell}
                          />
                        </td>
                      );
                    })}
                  </tr>

                  {row.showComment && group.comments !== CommentMode.OFF ? (
                    <tr className="comment-row">
                      <td colSpan={visibleColumns.length} className="recipe-comment-cell">
                        <input
                          type="text"
                          value={row.comment || ''}
                          onChange={(e) => {
                            if (!readonly && isGroupStore(group)) {
                              const mobxRow = group.rows[rowIndex];
                              if (mobxRow && 'setComment' in mobxRow) {
                                mobxRow.setComment(e.target.value);
                              }
                            }
                          }}
                          disabled={readonly}
                          className="recipe-comment-input"
                          placeholder="Enter comment..."
                        />
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </Box>

        {!readonly && isGroupStore(group) ? (
          <Box mt={1} display="flex" gap={1} flexWrap="wrap">
            {group.canAddGroup && group.rowType !== RowType.One ? (
              <Button
                ref={addRowButtonRef}
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddRow}
              >
                Add Row
              </Button>
            ) : null}
            {group.rows.length > 0 ? (
              <Button
                variant="outlined"
                size="small"
                color="warning"
                startIcon={<ClearIcon />}
                onClick={handleClearGroup}
              >
                Clear {group.name}
              </Button>
            ) : null}
            {group.allowSeveral && onAddGroup ? (
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={onAddGroup}>
                Add {group.name}
              </Button>
            ) : null}
            {group.allowSeveral && onRemoveGroup ? (
              <Button variant="outlined" size="small" color="error" startIcon={<RemoveIcon />} onClick={onRemoveGroup}>
                Remove {group.name}
              </Button>
            ) : null}
          </Box>
        ) : null}

        <Dialog open={rowPendingDelete != null} onClose={() => setRowPendingDelete(null)}>
          <DialogTitle>Remove row?</DialogTitle>
          <DialogContent>
            <DialogContentText>Remove this row?</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRowPendingDelete(null)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleConfirmRemoveRow}>
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={clearGroupRequested} onClose={() => setClearGroupRequested(false)}>
          <DialogTitle>Clear group?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {`Clear ${group.name}? This will remove all added rows and reset values in template rows.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setClearGroupRequested(false)}>Cancel</Button>
            <Button color="warning" variant="contained" onClick={handleConfirmClearGroup}>
              Clear
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
);

RecipeGroup.displayName = 'RecipeGroup';

export default RecipeGroup;
